/**
 * @fileoverview Modal for editing a budget's name and single bank mode setting.
 * Validates input (max 100 chars, no duplicates, trimmed, non-empty).
 */

import { useState, useEffect } from 'react';
import ToggleSwitch from '../shared/ToggleSwitch';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './BudgetManagement.module.css';

/**
 * EditBudgetModal Component
 *
 * Modal overlay for editing budget name and single bank mode with validation.
 *
 * Props:
 * - isOpen (boolean): Whether modal is visible
 * - budgetKey (string): Key of budget being edited
 * - currentName (string): Current budget name
 * - currentSingleBankMode (boolean): Current single bank mode setting
 * - onClose (function): Called when modal should close
 * - onSave (function): Called with new name and singleBankMode when save is clicked
 * - error (string): Error message to display
 */
function EditBudgetModal({ isOpen, budgetKey, currentName, currentSingleBankMode, onClose, onSave, error }) {
    const [budgetName, setBudgetName] = useState(currentName);
    const [singleBankMode, setSingleBankMode] = useState(currentSingleBankMode || false);
    const [isSaving, setIsSaving] = useState(false);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Reset saving state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
        }
    }, [isOpen]);

    // Update state when props change (when modal opens with a new budget)
    useEffect(() => {
        setBudgetName(currentName);
        setSingleBankMode(currentSingleBankMode || false);
    }, [currentName, currentSingleBankMode]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onSave(budgetKey, budgetName, singleBankMode);
            // If onSave succeeds and doesn't close the modal, we should reset
            // But typically the parent will close the modal on success
        } catch (error) {
            // Re-enable button on error
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <button
                    onClick={onClose}
                    disabled={isSaving}
                    className={styles.closeButton}
                    aria-label="Close"
                >
                    ×
                </button>

                <h2 className={styles.modalTitle}>Edit Budget</h2>

                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label htmlFor="budgetName" className={styles.label}>
                            Budget Name
                        </label>
                        <input
                            type="text"
                            id="budgetName"
                            value={budgetName}
                            onChange={(e) => setBudgetName(e.target.value)}
                            className={styles.input}
                            maxLength={100}
                            autoFocus
                        />
                        <div className={styles.helperText}>
                            {budgetName.length}/100 characters
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <ToggleSwitch
                            checked={singleBankMode}
                            onChange={(checked) => setSingleBankMode(checked)}
                            label="This budget uses only one bank account"
                        />
                        <div className={styles.helperText}>
                            When enabled, user categories must have zero balance and not be in the budget template.
                        </div>
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className={styles.saveButton}
                        >
                            {isSaving && <ButtonSpinner />}
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditBudgetModal;
